import React from 'react';
import { ArrowRight } from 'lucide-react';
import {
  howItWorksSteps,
  logoBySaasIntegrationName,
  SAAS_SIGNUP_URL,
} from './saasContent';

const sampleFlywheel = [
  { channel: 'Meta · Broad US', metric: 'ROAS 0.9×', verdict: 'Pause $1.8k/wk', tone: 'waste' },
  { channel: 'Onboarding · Step 3', metric: '68% complete', verdict: '89 nudges sent', tone: 'action' },
  { channel: 'Support · Billing', metric: '12m response', verdict: 'Auto-replied', tone: 'save' },
  { channel: 'Retain · Acme Corp', metric: 'High churn risk', verdict: 'Save playbook queued', tone: 'save' },
];

const SaasHowItWorksSection = () => {
  return (
    <section
      id="how-it-works"
      className="py-16 md:py-28 bg-gradient-to-b from-stone-50/90 via-white to-slate-50/40 overflow-x-hidden"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-stone-600 mb-3 md:mb-4">
            How it works
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-stone-900 leading-[1.12]">
            From signup to retention,
            {' '}
            <span className="bg-gradient-to-r from-[#6ee7a8] via-[#8ae690] to-[#5ec269] bg-clip-text text-transparent">
              on autopilot
            </span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto">
            Connect your stack, let the Company Brain learn, and ship growth without adding headcount.
          </p>
        </div>

        <div className="grid gap-6 sm:gap-7 lg:gap-8 md:grid-cols-1 lg:grid-cols-3">
          {howItWorksSteps.map(({ Icon, title, description, accent, logos, agents, actions }) => (
            <article
              key={title}
              className={`group relative flex flex-col gap-6 rounded-3xl border p-7 sm:p-8 md:p-9 lg:p-10 min-h-[23rem] sm:min-h-[24rem] shadow-[0_22px_60px_-28px_rgba(15,23,42,0.14)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_28px_72px_-32px_rgba(15,23,42,0.2)] ${accent.card}`}
            >
              <div
                className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] ring-1 sm:h-[4.5rem] sm:w-[4.5rem] md:h-20 md:w-20 ${accent.icon}`}
                aria-hidden
              >
                <Icon className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10" strokeWidth={1.55} aria-hidden />
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <h3 className="text-xl sm:text-2xl font-semibold text-stone-900 tracking-tight">{title}</h3>
                <p className="mt-3 text-base sm:text-lg text-stone-600 leading-relaxed">{description}</p>
                <div className="mt-5 flex flex-wrap items-center gap-2.5">
                  {logos?.map((name) => {
                    const src = logoBySaasIntegrationName[name];
                    if (!src) return null;
                    return (
                      <span
                        key={name}
                        className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 shadow-sm"
                      >
                        <img src={src} alt={`${name} logo`} className="h-9 w-auto object-contain" />
                        <span className="text-xs font-medium text-stone-700">{name}</span>
                      </span>
                    );
                  })}
                  {agents?.length ? (
                    <>
                      <span className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 shadow-sm">
                        <img src="/kolanut_logo.png" alt="Kolanut logo" className="h-9 w-9 object-contain" />
                        <span className="text-xs font-medium text-stone-700">Company Brain</span>
                      </span>
                      {agents.map((agent) => (
                        <span
                          key={agent}
                          className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-800"
                        >
                          {agent}
                        </span>
                      ))}
                    </>
                  ) : null}
                  {actions?.length ? (
                    <>
                      <span className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-3 py-1.5 shadow-sm">
                        <img src="/kolanut_logo.png" alt="Kolanut logo" className="h-9 w-9 object-contain" />
                        <span className="text-xs font-medium text-stone-700">Kolanut</span>
                      </span>
                      {actions.map((action) => (
                        <span
                          key={action}
                          className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800"
                        >
                          {action}
                        </span>
                      ))}
                    </>
                  ) : null}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 md:mt-10 rounded-3xl border border-emerald-200/80 bg-gradient-to-br from-white to-emerald-50/40 p-6 sm:p-8 shadow-[0_22px_60px_-28px_rgba(15,23,42,0.14)]">
          <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base text-stone-700 font-medium">
            <span className="rounded-full border border-stone-200 bg-white px-3 py-1.5">Product analytics</span>
            <ArrowRight className="h-4 w-4 text-stone-400" aria-hidden />
            <span className="rounded-full border border-stone-200 bg-white px-3 py-1.5">Company Brain</span>
            <ArrowRight className="h-4 w-4 text-stone-400" aria-hidden />
            <span className="rounded-full border border-stone-200 bg-white px-3 py-1.5">AI agents</span>
            <ArrowRight className="h-4 w-4 text-stone-400" aria-hidden />
            <span className="rounded-full border border-stone-200 bg-white px-3 py-1.5">Growth · Retention</span>
          </div>
          <div className="mt-5 rounded-2xl border border-[#c4ffa0]/60 bg-[#f4fbf4]/80 p-4 sm:p-5">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-[#2d4a1e]">
              Sample week with Kolanut
            </p>
            <div className="mt-3 rounded-xl border border-stone-200 bg-white">
              <div className="sm:hidden divide-y divide-stone-100">
                {sampleFlywheel.map((row) => (
                  <div key={row.channel} className="p-3">
                    <p className="text-sm font-semibold text-stone-900">{row.channel}</p>
                    <p className="mt-1.5 text-sm text-stone-700">{row.metric}</p>
                    <p
                      className={`mt-1 text-sm font-medium ${
                        row.tone === 'waste' ? 'text-red-700' : row.tone === 'action' ? 'text-violet-700' : 'text-emerald-700'
                      }`}
                    >
                      {row.verdict}
                    </p>
                  </div>
                ))}
              </div>
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-[720px] w-full text-left">
                  <thead className="bg-stone-50">
                    <tr className="text-xs sm:text-sm text-stone-600">
                      <th className="px-3 sm:px-4 py-2.5 font-semibold">Signal</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold">Metric</th>
                      <th className="px-3 sm:px-4 py-2.5 font-semibold">Agent action</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm sm:text-base text-stone-800">
                    {sampleFlywheel.map((row) => (
                      <tr key={row.channel} className="border-t border-stone-100">
                        <td className="px-3 sm:px-4 py-2.5 font-medium">{row.channel}</td>
                        <td className="px-3 sm:px-4 py-2.5">{row.metric}</td>
                        <td
                          className={`px-3 sm:px-4 py-2.5 font-medium ${
                            row.tone === 'waste' ? 'text-red-700' : row.tone === 'action' ? 'text-violet-700' : 'text-emerald-700'
                          }`}
                        >
                          {row.verdict}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1.5 text-sm sm:text-base font-semibold text-emerald-900">
              One brain · Marketing, onboarding & support in sync
            </p>
            <div className="mt-4 flex justify-center">
              <a
                href={SAAS_SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-12 md:h-14 px-8 md:px-10 lg:px-14 rounded-full bg-[#8ae690] text-black font-semibold text-sm md:text-base shadow-md hover:bg-[#7ad67f] hover:shadow-lg transition-all duration-200"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/70">
                  <img src="/kolanut_logo.png" alt="Kolanut logo" className="w-4 h-4 object-contain" />
                </span>
                Activate your growth flywheel
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SaasHowItWorksSection;
